/**
 * MessageMedia Component
 * Display media content (image, video, audio, document)
 * Uses MediaProxy and AudioPlayer for authenticated WAHA media
 */

import { memo, useState, useCallback } from 'react';
import { FileText, Download, Loader2 } from 'lucide-react';
import { MediaProxy } from './MediaProxy';
import { AudioPlayer } from './AudioPlayer';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { callWahaProxy } from '@/lib/services/wahaProxyService';
import type { MessageType } from '@/lib/types/whatsappChat';

interface MessageMediaProps {
  type: MessageType;
  mediaUrl?: string;
  mediaMimetype?: string;
  mediaFilename?: string;
  onImageClick?: () => void;
  timestamp?: string;
  avatarUrl?: string;
  avatarName?: string;
}

function DocumentMedia({ url, filename }: { url: string; filename?: string }) {
  const [downloading, setDownloading] = useState(false);
  const { wahaConfig } = useWhatsAppChatStore();

  const handleDownload = useCallback(async () => {
    if (downloading) return;

    setDownloading(true);
    try {
      // Check if needs authentication (WAHA media URL)
      const isWahaUrl = url.includes('/api/files/') || url.includes('/api/media/') ||
        (wahaConfig?.apiUrl && url.startsWith(wahaConfig.apiUrl));

      let response: Response;
      if (isWahaUrl) {
        const urlObj = new URL(url);
        const path = urlObj.pathname + urlObj.search;
        response = await callWahaProxy({ method: 'GET', path });
      } else {
        response = await fetch(url);
      }

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create temp link to download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'documento';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('[DocumentMedia] Download error:', err);
    } finally {
      setDownloading(false);
    }
  }, [url, filename, wahaConfig, downloading]);

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="flex items-center gap-3 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg px-3 py-2 hover:bg-[rgba(55,53,47,0.08)] transition-colors w-full text-left"
    >
      <FileText className="w-8 h-8 text-[#2383E2] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">
          {filename || 'Documento'}
        </p>
        <p className="text-xs text-[#787774]">Documento</p>
      </div>
      {downloading ? (
        <Loader2 className="w-4 h-4 text-[#787774] animate-spin flex-shrink-0" />
      ) : (
        <Download className="w-4 h-4 text-[#787774] flex-shrink-0" />
      )}
    </button>
  );
}

export const MessageMedia = memo(function MessageMedia({
  type,
  mediaUrl,
  mediaMimetype,
  mediaFilename,
  onImageClick,
  timestamp,
  avatarUrl,
  avatarName,
}: MessageMediaProps) {
  if (!mediaUrl) return null;

  // Determina il tipo effettivo basandosi anche sul mimetype (fallback robusto)
  const isAudio = type === 'audio' ||
                  type === 'ptt' ||
                  type === 'voice' ||
                  mediaMimetype?.startsWith('audio/');

  const isSticker = type === 'sticker';

  const isImage = type === 'image' ||
                  mediaMimetype?.startsWith('image/');

  const isVideo = type === 'video' ||
                  mediaMimetype?.startsWith('video/');

  // Sticker - senza timestamp overlay (gestito da MessageBubble)
  if (isSticker) {
    return <MediaProxy url={mediaUrl} type="image" onClick={onImageClick} isSticker />;
  }

  // Immagine normale - con bordo e timestamp overlay
  if (isImage) {
    return <MediaProxy url={mediaUrl} type="image" onClick={onImageClick} timestamp={timestamp} />;
  }

  if (isVideo) {
    return <MediaProxy url={mediaUrl} type="video" />;
  }

  if (isAudio) {
    return (
      <AudioPlayer
        url={mediaUrl}
        isVoiceNote={type === 'ptt' || type === 'voice'}
        avatarUrl={avatarUrl}
        avatarName={avatarName}
        timestamp={timestamp}
      />
    );
  }

  if (type === 'document') {
    return <DocumentMedia url={mediaUrl} filename={mediaFilename} />;
  }

  // Fallback: se c'è un mediaUrl ma tipo non riconosciuto, trattalo come documento
  return <DocumentMedia url={mediaUrl} filename={mediaFilename} />;
});
